import {
  Component,
  OnInit,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { BuyerSurveyResponse } from '../../shared/interfaces/survey.interface';
import { CountryInterface } from '../../shared/interfaces/create-surveys.interface';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { SURVEY_STATUS } from '../../shared/constants/survey-status';
import { QuotaPurePrice } from '../pure-price-window/pure-price.interface';
import { MetaDataToPurePrice } from '../../shared/interfaces/black-box.interface';
import { AuthService } from '../../shared/services/auth/auth.service';
import { filter, map } from 'rxjs/operators';
import { SurveyPayload } from '../../shared/interfaces/survey-meta-data.interface';
import { UnicornQuotas } from '../../shared/interfaces/unicorn.interface';

@Component({
  selector: 'ps-ui-right-slider-window',
  templateUrl: './right-slider-window.component.html',
  styleUrls: ['./right-slider-window.component.css'],
})
export class RightSliderWindowComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() surveyDetails = <BuyerSurveyResponse>{};
  @Input() surveyPayload = <SurveyPayload>{};
  @Input() public purePriceData!: MetaDataToPurePrice;
  @Input() hasQuotasForPurePrice: boolean = false;
  @Input() isTCSPAEditMode: boolean = false;
  @Input() countries: CountryInterface[] = [];
  @Input() showFlyoutWindow: boolean = false;
  @Input() selectedTemplateId?: number | null;
  @Input() unicornQuotas: UnicornQuotas[] = [];
  @Input() difficultQuotas: any = [];

  @Output() purePriceApplied = new EventEmitter<QuotaPurePrice>();
  @Output() purePriceV2Applied = new EventEmitter<number>();
  @Output() mcSurveyAdded = new EventEmitter();
  @Output() cancelTemplateSelection = new EventEmitter();

  public readonly surveyStatus = SURVEY_STATUS;
  public appliedQuotaName?: string;
  public surveyId!: number;
  public serviceOperator: boolean =
    this._authService.isLoggedInAsServiceBuyer();

  public isFlyoutDrawnOut: boolean = true;
  public shouldFlyoutExist$!: Observable<boolean>;
  public isAidaEnabled: boolean = false;

  private _subscriptions: Subscription = new Subscription();
  private _surveyDetailsCommand$ = new EventEmitter<BuyerSurveyResponse>();
  private _whenMcDataPresentCommand$ = new EventEmitter<boolean>();

  constructor(
    private _route: ActivatedRoute,
    private _authService: AuthService
  ) {}

  ngOnInit(): void {
    this._handleRouteChanges();
    this._decideFlyoutStateAndData();
    this._handleSurveyDetailsChanges();
    this.isAidaEnabled =
      !!this._authService.buyerConfig?.enableAiDeliveryAssistance;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.surveyDetails?.currentValue) {
      this._surveyDetailsCommand$.next(this.surveyDetails);
    }
  }

  private _handleRouteChanges() {
    this._subscriptions.add(
      this._route.params
        .pipe(filter((params) => params && params.id))
        .subscribe((params) => {
          this.surveyId = params.id;
        })
    );
  }

  private _decideFlyoutStateAndData() {
    this.shouldFlyoutExist$ = combineLatest([
      this._whenMcDataPresentCommand$,
    ]).pipe(map(([shouldMcFlyoutExist]) => shouldMcFlyoutExist));
  }

  private _handleSurveyDetailsChanges() {
    // Applied quota name
    this._subscriptions.add(
      this._surveyDetailsCommand$
        .pipe(filter((surveyData) => !!surveyData))
        .subscribe((surveyData) => {
          this.appliedQuotaName = surveyData.qbp?.category;
        })
    );
  }

  applyPurePrice(quotaPurePriceApplied: QuotaPurePrice) {
    this.purePriceApplied.emit(quotaPurePriceApplied);
  }
  public applyPurePriceV2(fieldTime: number) {
    this.purePriceV2Applied.emit(fieldTime);
  }

  public whenMcDataPresent(isPresent: boolean) {
    this._whenMcDataPresentCommand$.next(isPresent);
  }

  public drawFlyout(direction: 'in' | 'out') {
    this.isFlyoutDrawnOut = direction === 'out';
  }

  public informOnMcSurveyAdded() {
    this.mcSurveyAdded.emit();
  }

  public getTopMargin() {
    let top = 60;
    if (this.serviceOperator) {
      top = 100;
    }
    if (this.unicornQuotas.length) {
      top += 58;
      if (this.isAidaEnabled) {
        top += 37;
      }
    }
    if (this.difficultQuotas.length) {
      top += 58;
    }
    return `${top}px`;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
